import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Divider,
  Button,
  Badge,
  TextField,
} from '@mui/material';
import PropTypes from 'prop-types';
import { memo, useCallback, useState } from 'react';
import history from 'store/history';
import NoteService from 'Services/NoteService';
import { formatUSPhone } from 'utils/formatters/phone';

const PhoneCallNotification = ({
  open,
  customer,
  onClose,
  remainingCount = 0,
  callId,
}) => {
  const handleViewCustomer = useCallback(() => {
    if (!customer?.customer_id) {
      return;
    }

    if (customer.customer_type === 'App\\Company') {
      history.push(`/company/${customer.customer_id}`);
    } else if (customer.customer_type === 'App\\Contact') {
      history.push(`/contact/${customer.customer_id}`);
    }
    onClose();
  }, [customer, onClose]);

  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteId, setNoteId] = useState(null);

  const handleToggleAddNote = useCallback(() => {
    setIsAddingNote((prev) => !prev);
  }, []);

  const handleNoteChange = useCallback((event) => {
    setNoteContent(event.target.value);
  }, []);

  const handleSaveNote = useCallback(async () => {
    if (!callId || !noteContent.trim()) {
      return;
    }

    setIsSavingNote(true);

    const payload = {
      content: `<p>${noteContent.trim()}</p>`,
    };

    try {
      if (noteId) {
        await NoteService.updateNote({
          id: noteId,
          content: payload.content,
        });
      } else {
        const res = await NoteService.createNote({
          class: 'Note',
          title: '',
          model: 'CustomerCallHistory',
          model_id: callId,
          urgent: 0,
          content: payload.content,
          createdBy: '',
        });

        const created = res?.object || res;
        if (created && created.id) {
          setNoteId(created.id);
        }
      }
    } catch (error) {
      // ignore, errors are handled globally elsewhere
    } finally {
      setIsSavingNote(false);
    }
  }, [callId, noteContent, noteId]);

  if (!customer) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          position: 'fixed',
          bottom: 16,
          right: 16,
          margin: 0,
          maxWidth: 400,
          width: '100%',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          pointerEvents: 'auto',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'transparent',
          pointerEvents: 'none',
        },
      }}
      sx={{
        pointerEvents: 'none',
        '& .MuiDialog-container': {
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          pointerEvents: 'none',
        },
        '& .MuiDialog-paper': {
          pointerEvents: 'auto',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 2,
          pt: 2.5,
          px: 2.5,
          position: 'relative',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ flex: 1, pr: 4, position: 'relative' }}>
          {remainingCount > 0 && (
            <Badge
              badgeContent={remainingCount}
              color="error"
              sx={{
                position: 'absolute',
                top: -8,
                left: -8,
                '& .MuiBadge-badge': {
                  fontSize: '0.75rem',
                  minWidth: '20px',
                  height: '20px',
                  padding: '0 6px',
                },
              }}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 20 }} />
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Incoming Call
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
            {customer.name || 'Unknown Customer'}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, px: 2.5, pb: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                mb: 0.5,
                display: 'block',
              }}
            >
              Contact Name
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {customer.name || 'N/A'}
            </Typography>
          </Box>
          <Divider />
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                mb: 0.5,
                display: 'block',
              }}
            >
              Email
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {customer.email || 'N/A'}
            </Typography>
          </Box>
          <Divider />
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                mb: 0.5,
                display: 'block',
              }}
            >
              Phone
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatUSPhone(customer.phone) || 'N/A'}
            </Typography>
          </Box>
          <Divider />
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                mb: 0.5,
                display: 'block',
              }}
            >
              Cell Phone
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatUSPhone(customer.cell_phone) || 'N/A'}
            </Typography>
          </Box>
          {customer.customer_id && (
            <>
              <Divider />
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleViewCustomer}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    textTransform: 'none',
                    py: 1.25,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #653a8f 100%)',
                    },
                  }}
                >
                  View {customer.customer_type === 'App\\Company' ? 'Company' : 'Contact'}
                </Button>
              </Box>
            </>
          )}
          {callId && (
            <>
              <Divider />
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {isAddingNote && (
                  <TextField
                    multiline
                    minRows={3}
                    maxRows={6}
                    variant="outlined"
                    label="Add Note"
                    value={noteContent}
                    onChange={handleNoteChange}
                  />
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  {isAddingNote && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveNote}
                      disabled={isSavingNote || !noteContent.trim()}
                    >
                      {isSavingNote ? 'Saving...' : 'Save Note'}
                    </Button>
                  )}
                  <Button variant="outlined" onClick={handleToggleAddNote}>
                    {isAddingNote ? 'Cancel' : 'Add Note'}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

PhoneCallNotification.propTypes = {
  open: PropTypes.bool.isRequired,
  customer: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    type: PropTypes.string,
    customer_id: PropTypes.number,
    customer_type: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    cell_phone: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  remainingCount: PropTypes.number,
  callId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default memo(PhoneCallNotification);
